import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { Table } from 'antd'
import styled from 'styled-components'

import _ from 'lodash'

import { useModuleIds } from 'portal/lib/hooks'
import { colours } from '@qflow/theme'

import {
  DetailRow,
  AttachmentsSection,
  ValueAdd
} from 'portal/components/RecordDetails'
import {
  convertAssignedToArrayToString,
  DateFormatFull
} from 'portal/lib/functions'
import { Flex, SubHeading, Spacer, PillBox } from 'portal/lib/primitives'
import {
  RemoveRecordButton,
  EditRecordButton,
  LineItemButtons,
  AddLineItemButton,
  InfractionFlag,
  ViewInAdminButton
} from 'portal/components/RecordsModules/common'
import { AttachmentsHeader } from 'portal/components/RecordDetails/AttachmentsHeader'
import { useInfractionGetter } from 'portal/store/infractions/InfractionsContext'

export function Waste({ record }) {
  const { accountId, moduleName } = useModuleIds()
  const getInfraction = useInfractionGetter()

  const projectTimeZone = useSelector(
    state => state.projectDetails.modules?.timeZone
  )

  const projectIsProLicence = useSelector(
    state => state.projectDetails.modules?.isProLicence
  )

  let assignedTo = ''

  if (record.assignedTo) {
    assignedTo = convertAssignedToArrayToString(record.assignedTo)
  }

  const dataForItemsTable = useMemo(() => {
    let items = []
    if (record?.data?.items) {
      const originalItems = [...record.data.items]
      originalItems.forEach((element, index) => {
        items.push({ originalIndex: index, ...element })
      })
      items = items.filter(p => !p.isDeleted)
    }
    if (record?.dataSourceTracking?.items) {
      items = items.map((item, index) => {
        const dataSourceItem = record.dataSourceTracking.items[index]
        if (dataSourceItem) {
          return {
            ...item,
            valueAdd: dataSourceItem
          }
        }
        return item
      })
    }
    return items
  }, [record])

  const itemTableHeaders = useMemo(() => {
    const columns = [
      {
        key: 'containerType-ValueAdd',
        dataIndex: 'valueAdd',
        render: item => {
          if (!item) {
            return null
          }
          return (
            <NegativeMargin>
              <ValueAdd
                details={{ fieldName: 'containerType', ...item.containerType }}
              />
            </NegativeMargin>
          )
        },
        width: '1px'
      },
      {
        key: 'containerType',
        title: 'Container Type',
        dataIndex: 'containerType'
      },
      {
        key: '__issues__',
        title: 'Issues',
        render: (item, record, index) => {
          const infractions = getInfraction(`data.items[${index}]`, {
            isPrefix: true
          })

          if (!infractions?.length) {
            return null
          }

          return (
            <PillBox>
              {infractions.map((v, i) => (
                <InfractionFlag key={i} />
              ))}
            </PillBox>
          )
        }
      },
      {
        key: 'wasteCode-ValueAdd',
        dataIndex: 'valueAdd',
        render: item => {
          if (!item) {
            return null
          }
          return (
            <NegativeMargin>
              <ValueAdd
                details={{ fieldName: 'wasteCode', ...item.wasteCode }}
              />
            </NegativeMargin>
          )
        },
        width: '1px'
      },
      { key: 'wasteCode', title: 'Waste Code (EWC)', dataIndex: 'wasteCode' },
      {
        key: 'volumeEstimated-ValueAdd',
        dataIndex: 'valueAdd',
        render: item => {
          if (!item) {
            return null
          }
          return (
            <NegativeMargin>
              <ValueAdd
                details={{
                  fieldName: 'volumeEstimated',
                  ...item.volumeEstimated
                }}
              />
            </NegativeMargin>
          )
        },
        width: '1px'
      },
      {
        key: 'volumeEstimated',
        title: (
          <>
            Estimated Volume{' '}
            <span>
              (m<sup>3</sup>)
            </span>
          </>
        ),
        dataIndex: 'volumeEstimated'
      },
      {
        key: 'volume-ValueAdd',
        dataIndex: 'valueAdd',
        render: item => {
          if (!item) {
            return null
          }
          return (
            <NegativeMargin>
              <ValueAdd details={{ fieldName: 'volume', ...item.volume }} />
            </NegativeMargin>
          )
        },
        width: '1px'
      },
      {
        key: 'volume',
        title: (
          <>
            Volume{' '}
            <span>
              (m<sup>3</sup>)
            </span>
          </>
        ),
        dataIndex: 'volume'
      },
      {
        key: 'weightEstimated-ValueAdd',
        dataIndex: 'valueAdd',
        render: item => {
          if (!item) {
            return null
          }
          return (
            <NegativeMargin>
              <ValueAdd
                details={{
                  fieldName: 'weightEstimated',
                  ...item.weightEstimated
                }}
              />
            </NegativeMargin>
          )
        },
        width: '1px'
      },
      {
        key: 'weightEstimated',
        title: (
          <>
            Estimated Weight <span>(t)</span>
          </>
        ),
        dataIndex: 'weightEstimated'
      },
      {
        key: 'weight-ValueAdd',
        dataIndex: 'valueAdd',
        render: item => {
          if (!item) {
            return null
          }
          return (
            <NegativeMargin>
              <ValueAdd details={{ fieldName: 'weight', ...item.weight }} />
            </NegativeMargin>
          )
        },
        width: '1px'
      },
      {
        key: 'weight',
        title: (
          <>
            Weight <span>(t)</span>
          </>
        ),
        dataIndex: 'weight'
      },
      {
        key: 'quantity-ValueAdd',
        dataIndex: 'valueAdd',
        render: item => {
          if (!item) {
            return null
          }
          return (
            <NegativeMargin>
              <ValueAdd details={{ fieldName: 'quantity', ...item.quantity }} />
            </NegativeMargin>
          )
        },
        width: '1px'
      },
      { key: 'quantity', title: 'Quantity', dataIndex: 'quantity' },
      {
        key: 'actions',
        title: '',
        render: (item, product, index) =>
          !item.isDeleted && record.data?.items[item.originalIndex] ? (
            <div style={{ display: 'flex' }}>
              <LineItemButtons record={record} index={item.originalIndex} />
            </div>
          ) : (
            `${item.originalIndex}`
          ),
        width: '52px',
        style: { display: 'flex' }
      }
    ]
    return columns
  }, [getInfraction, record])

  return (
    <>
      <MainSection>
        <Flex.Row amount={0} verticalCentre>
          <SubHeading
            id="WasteDetailHeading"
            isChild
            style={{ fontSize: '1.125rem', color: colours.CORE_NAVY }}
          >
            Waste Record
          </SubHeading>

          <Flex.By amount={1} />
          {record.canEdit && (
            <Flex.By amount={0}>
              <ViewInAdminButton
                record={record}
                accountId={accountId}
                moduleName={moduleName}
              />
              <Spacer.Tiny />
              <EditRecordButton />
              <Spacer.Tiny />
              <RemoveRecordButton />
            </Flex.By>
          )}
        </Flex.Row>

        <SubSection style={{ marginTop: '1.25rem' }}>
          <SubSectionHeading>General</SubSectionHeading>
          <DetailRow
            label="Assigned To"
            content={assignedTo}
            testId={'assigned-to'}
            valueAdd={{
              fieldName: 'assignedTo',
              ...record.dataSourceTracking?.assignedTo
            }}
          />

          <DetailRow
            label="Record created"
            content={
              record.createdAt
                ? new Date(record.createdAt).toLocaleString(
                    [],
                    DateFormatFull(projectTimeZone)
                  )
                : null
            }
          />

          <DetailRow
            label="Arrival date and time"
            infraction={getInfraction('data.removalDate')}
            content={new Date(record.data.removalDate).toLocaleString(
              [],
              DateFormatFull(projectTimeZone)
            )}
            valueAdd={{
              fieldName: 'removalDate',
              ...record.dataSourceTracking?.removalDate
            }}
          />

          <DetailRow
            label="Exit date and time"
            infraction={getInfraction('data.removalExitDate')}
            content={
              record.data.removalExitDate
                ? new Date(record.data.removalExitDate).toLocaleString()
                : null
            }
            valueAdd={{
              fieldName: 'removalExitDate',
              ...record.dataSourceTracking?.removalExitDate
            }}
          />

          <DetailRow
            label="Consignment number (WTN)"
            content={record.data.consignmentId}
            infraction={getInfraction('data.consignmentId')}
            valueAdd={{
              fieldName: 'consignmentId',
              ...record.dataSourceTracking?.consignmentId
            }}
          />

          <DetailRow
            label="Sender reference"
            content={record.data.receiverReferenceNumber}
            infraction={getInfraction('data.receiverReferenceNumber')}
            valueAdd={{
              fieldName: 'receiverReferenceNumber',
              ...record.dataSourceTracking?.receiverReferenceNumber
            }}
          />

          <DetailRow
            label="Subcontractor"
            content={record.data.subcontractorName}
            infraction={getInfraction('data.subcontractorName')}
            valueAdd={{
              fieldName: 'subcontractorName',
              ...record.dataSourceTracking?.subcontractorName
            }}
          />

          <DetailRow
            label="Origin site address"
            content={record.data.originSiteAddressKnown}
            infraction={getInfraction('data.originSiteAddressKnown')}
            valueAdd={{
              fieldName: 'originSiteAddressKnown',
              ...record.dataSourceTracking?.originSiteAddressKnown
            }}
          />

          <DetailRow
            label="SIC code"
            content={record.data.classificationCode}
            infraction={getInfraction('data.classificationCode')}
            valueAdd={{
              fieldName: 'classificationCode',
              ...record.dataSourceTracking?.classificationCode
            }}
          />

          <DetailRow
            label="SIC code provided"
            content={record.data.classificationCodePresented ? 'Yes' : 'No'}
            valueAdd={{
              fieldName: 'classificationCodePresented',
              ...record.dataSourceTracking?.classificationCodePresented
            }}
          />

          <DetailRow
            label="Vehicle reg"
            content={record.data.vehicleRegistration}
            infraction={getInfraction('data.vehicleRegistration')}
            valueAdd={{
              fieldName: 'vehicleRegistration',
              ...record.dataSourceTracking?.vehicleRegistration
            }}
          />
        </SubSection>
        <SubSection>
          <SubSectionHeading>Carrier</SubSectionHeading>
          <DetailRow
            label="Carrier name"
            content={record.data.carrierName}
            infraction={getInfraction('data.carrierName')}
            valueAdd={{
              fieldName: 'carrierName',
              ...record.dataSourceTracking?.carrierName
            }}
          />

          <DetailRow
            label="Reference"
            content={record.data.supplierReferenceNumber}
            infraction={getInfraction('data.supplierReferenceNumber')}
            valueAdd={{
              fieldName: 'supplierReferenceNumber',
              ...record.dataSourceTracking?.supplierReferenceNumber
            }}
          />

          <DetailRow
            label="Licence"
            content={record.data.carrierLicenceId}
            infraction={getInfraction('data.carrierLicenceId')}
            valueAdd={{
              fieldName: 'carrierLicenceId',
              ...record.dataSourceTracking?.carrierLicenceId
            }}
          />
        </SubSection>
        <SubSection>
          <SubSectionHeading>Facility</SubSectionHeading>
          <DetailRow
            label="Operator"
            content={record.data.facilityOperatorName}
            infraction={getInfraction('data.facilityOperatorName')}
            valueAdd={{
              fieldName: 'facilityOperatorName',
              ...record.dataSourceTracking?.facilityOperatorName
            }}
          />

          <DetailRow
            label="Site address"
            content={record.data.facilitySiteAddress}
            infraction={getInfraction('data.facilitySiteAddress')}
            valueAdd={{
              fieldName: 'facilitySiteAddress',
              ...record.dataSourceTracking?.facilitySiteAddress
            }}
          />

          <DetailRow
            label="Permit"
            content={record.data.facilityPermitId}
            infraction={getInfraction('data.facilityPermitId')}
            valueAdd={{
              fieldName: 'facilityPermitId',
              ...record.dataSourceTracking?.facilityPermitId
            }}
          />

          <DetailRow
            label="Permit provided"
            content={record.data.facilityPermitPresented ? 'Yes' : 'No'}
            infraction={getInfraction('data.facilityPermitPresented')}
            valueAdd={{
              fieldName: 'facilityPermitPresented',
              ...record.dataSourceTracking?.facilityPermitPresented
            }}
          />

          <DetailRow
            label="External reference"
            content={record.data.clientReference}
            infraction={getInfraction('data.clientReference')}
            valueAdd={{
              fieldName: 'clientReference',
              ...record.dataSourceTracking?.clientReference
            }}
          />

          <DetailRow
            label="Notes"
            content={record.data.notes}
            infraction={getInfraction('data.notes')}
            valueAdd={{
              fieldName: 'notes',
              ...record.dataSourceTracking?.notes
            }}
          />
        </SubSection>
        <SubSection>
          <SubSectionHeading>Journey</SubSectionHeading>
          <DetailRow
            label="Journey distance (mi)"
            content={
              record.data.journey?.distanceMiles
                ? _.round(record.data.journey.distanceMiles, 2).toString()
                : null
            }
            infraction={getInfraction('data.journey.distanceMiles')}
            tooltip="Journey distance represents the laden journey only"
            valueAdd={{
              fieldName: 'distanceMiles',
              ...record.dataSourceTracking?.journey
            }}
          />
          <DetailRow
            label={
              <span>
                Laden journey emissions (kg CO<sub>2</sub>e)
              </span>
            }
            content={
              record.data.journey?.ladenEmissionsKgCO2e
                ? _.round(
                    record.data.journey.ladenEmissionsKgCO2e,
                    2
                  ).toString()
                : null
            }
            infraction={getInfraction('data.journey.ladenEmissionsKgCO2e')}
            upgradeToPro={!projectIsProLicence}
            valueAdd={{
              fieldName: 'ladenEmissionsKgCO2e',
              ...record.dataSourceTracking?.journey
            }}
          />
          <DetailRow
            label={
              <span>
                Unladen journey emissions (kg CO<sub>2</sub>e)
              </span>
            }
            content={
              record.data.journey?.unladenEmissionsKgCO2e
                ? _.round(
                    record.data.journey.unladenEmissionsKgCO2e,
                    2
                  ).toString()
                : null
            }
            infraction={getInfraction('data.journey.unladenEmissionsKgCO2e')}
            upgradeToPro={!projectIsProLicence}
            valueAdd={{
              fieldName: 'unladenEmissionsKgCO2e',
              ...record.dataSourceTracking?.journey
            }}
          />
        </SubSection>
      </MainSection>

      <MainSection>
        <AttachmentsHeader record={record} />
        <AttachmentsSection record={record} attachments={record.attachments} />
      </MainSection>

      <MainSection>
        <Flex.Row amount={0} verticalCentre style={{ gap: '1.5rem' }}>
          <SubHeading
            isChild
            style={{ fontSize: '1.125rem', color: colours.CORE_NAVY }}
          >
            Items
          </SubHeading>

          {record.canEdit && <AddLineItemButton />}
        </Flex.Row>
        <SubSection style={{ marginTop: '1.25rem', paddingTop: '0' }}>
          <TableSection>
            <Table
              id="items-table"
              dataSource={dataForItemsTable ?? []}
              rowKey={row => row.id}
              columns={itemTableHeaders}
              style={{ borderCollapse: 'separate', borderSpacing: '0 5px' }}
              pagination={false}
              locale={{ emptyText: 'No items' }}
              onRow={(record, index) => ({
                style: record.isRemoved
                  ? {
                      textDecoration: 'line-through',
                      color: colours.BATTLESHIP_GREY
                    }
                  : {}
              })}
            />
          </TableSection>
        </SubSection>
      </MainSection>
    </>
  )
}

Waste.propTypes = {
  record: PropTypes.object.isRequired
}

const MainSection = styled.div`
  padding: 1.25rem 1.25rem 1.5rem 1.375rem;
  margin: 0 0 1.25rem -0.5rem;
  box-shadow: 0px 1px 3px 0px #00000026;
  background-color: ${colours.WHITE};
  border-radius: 10px;
`
const SubSection = styled.div`
  padding: 1rem 1.25rem 1.125rem;
  background-color: ${colours.CONTROL_PANEL_GREY};
  border-radius: 10px;
  margin-top: 0.625rem;
`
const SubSectionHeading = styled.h3`
  color: ${colours.CORE_NAVY};
  font-size: 0.9375rem;
  font-family:
    'Jakarta Sans',
    'Roboto',
    'Oxygen',
    'Ubuntu',
    'Helvetica Neue',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    sans-serif !important;
  font-weight: 800;
  line-height: 1.375rem;
`
const TableSection = styled.div`
  .ant-table-thead > tr > th,
  .ant-table-tbody > tr > td,
  .ant-table tfoot > tr > th,
  .ant-table tfoot > tr > td {
    padding: 0.5rem;
  }

  .ant-table-thead > tr > th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    span,
    sup {
      text-transform: none;
      letter-spacing: initial;
    }
  }
`
const NegativeMargin = styled.div`
  margin: 0 -0.5rem;
`
